import { useEffect, useRef } from 'react'
import { useIsPresent } from 'framer-motion'
import {
  useLoadMessages,
  useSetActiveChat,
  useCloseActiveChat,
  useActiveChatId,
  useChatMessageCount,
  useGetRealChatId,
  useIsTurnLive,
  useRemoveChatIdMapping,
} from '@renderer/chat'
import { useClaimConversation } from '../../hooks/use-claim-conversation'
import type { ChatViewContext } from './use-chat-view-model'

interface ChatIdReconciliation {
  /** The globally-active chat, which the view follows through the temp→real swap. */
  activeChatId: string | undefined
  /**
   * How many messages the resolved chat holds. A count, not the transcript:
   * the view's chrome only gates on "is it loaded", and subscribing to the
   * messages themselves re-rendered the whole view on every streaming token.
   */
  messageCount: number
  /** The chat this view is showing: its own context id, falling back to the active one. */
  currentChatId: string | undefined
  /** `currentChatId` mapped through the temp→real table — the id the stores key on. */
  resolvedChatId: string | undefined
  /** Fetch state, for the callers that gate on "settled and still empty". */
  isFetched: boolean
  isFetching: boolean
  loadedMessageCount: number
}

/**
 * The Chat View's id bookkeeping: adopting the chat it was opened for, and
 * reconciling the temp UUID a fresh submit mints against the real backend id
 * that arrives moments later.
 *
 * This is the fiddliest part of the view and it is all one concern — the refs
 * below only make sense to each other. Everything here was ported from the
 * legacy chat drawer unchanged apart from writing through the view router.
 */
export function useChatIdReconciliation(
  context: ChatViewContext | undefined,
  updateChatContext: (update: (context: ChatViewContext | undefined) => ChatViewContext | undefined) => void,
): ChatIdReconciliation {
  const setActiveChat = useSetActiveChat()
  const closeActiveChat = useCloseActiveChat()
  const activeChatId = useActiveChatId()
  const isNewChat = context?.isNew ?? false
  const keepsAttachments = context?.sendsComposerAttachments ?? false

  const { refetch, isFetching, isFetched, data: loadedMessages } = useLoadMessages()
  const getRealChatId = useGetRealChatId()
  const removeChatIdMapping = useRemoveChatIdMapping()
  // Use activeChatId for messages query - handles recovery flow where context has stale temp ID
  const messageCount = useChatMessageCount(activeChatId ?? context?.chatId)

  const currentChatId = context?.chatId ?? activeChatId
  // Resolve temp → real id before any streaming-store lookup. After a temp→real
  // swap the store (and `_completedChats`) key everything by the real id, but
  // currentChatId can still be the temp UUID until the context update lands —
  // so useIsTurnLive(tempId) reads false and removeCompletedTurn(tempId)
  // misses the real-keyed entry. The temp→real mapping is populated alongside the
  // swap (useSubmitMessage), so getRealChatId resolves it during a live stream.
  const resolvedChatId = currentChatId ? getRealChatId(currentChatId) : undefined

  const lastActiveChatIdRef = useRef<string | undefined>(activeChatId)
  const refetchAttemptedRef = useRef<string | undefined>()
  const tempChatIdRef = useRef<string | undefined>()

  // Handle new chat mode - clear active chat. Runs whenever isNewChat becomes
  // true (not just on mount) so that reusing the mounted view for a fresh
  // chat resets activeChatId/lastActiveChatIdRef; otherwise the first temp
  // UUID transition looks like a real-backend-ID event and clears isNew early.
  // Also whenever the view is back on top: navigating back mid-exit re-presents
  // the same instance, while the chat it left for still holds activeChatId. A
  // view that already sent, and still waits for the real id, takes its own
  // chat back instead, so the reset doesn't drop the send.
  const isTop = useIsPresent()
  const isTopRef = useRef(isTop)
  isTopRef.current = isTop
  const sentChatIdRef = useRef<string | undefined>()
  const getRealChatIdRef = useRef(getRealChatId)
  getRealChatIdRef.current = getRealChatId
  useEffect(() => {
    if (!isNewChat) {
      sentChatIdRef.current = undefined
      return
    }
    if (!isTop) return
    const sentChatId = sentChatIdRef.current
    if (sentChatId) {
      // From the temp id, as if the chat never left: the real id, if it has
      // landed, then reads as the second step and the view adopts it.
      lastActiveChatIdRef.current = sentChatId
      setActiveChat(getRealChatIdRef.current(sentChatId))
      return
    }
    closeActiveChat({ keepAttachments: keepsAttachments })
  }, [isNewChat, isTop, keepsAttachments, closeActiveChat, setActiveChat])

  // Claim the chat the view opened with (and re-claim it when the idle
  // timeout clears it) — the shared surface policy, see the hook's doc.
  useClaimConversation(context?.chatId, isNewChat)

  // Ensure messages are loaded when reopening the view with an existing chatId
  useEffect(() => {
    // For new chats, only proceed if we have a temp ID to clean up (after transition)
    const hasTempIdToCleanup = Boolean(tempChatIdRef.current)
    if (isNewChat && !hasTempIdToCleanup) return

    const chatId = context?.chatId ?? activeChatId
    if (!chatId) return

    // Skip if already fetching to avoid redundant API calls
    if (isFetching) return

    // Check if we're in recovery mode (context has stale temp ID, but we have real ID in activeChatId)
    const isRecoveryMode = tempChatIdRef.current && activeChatId && activeChatId !== chatId

    // If messages are loaded (for activeChatId after recovery, or for chatId in normal flow)
    if (messageCount > 0) {
      // Clean up mapping using the original temp ID (not the real ID from context)
      if (tempChatIdRef.current) {
        removeChatIdMapping(tempChatIdRef.current)
        tempChatIdRef.current = undefined
      }
      refetchAttemptedRef.current = undefined
      return
    }

    // For refetch, wait until activeChatId matches chatId (normal flow only)
    // In recovery mode, we're already using activeChatId for messages query
    if (!isRecoveryMode && activeChatId !== chatId) return

    // Only attempt refetch once per chatId to avoid infinite loops
    const refetchKey = activeChatId ?? chatId
    if (refetchAttemptedRef.current === refetchKey) return

    // Trigger a refetch to load messages from API
    refetchAttemptedRef.current = refetchKey
    refetch()
  }, [isNewChat, context?.chatId, activeChatId, messageCount, refetch, isFetching, removeChatIdMapping])

  // Safety net for a chat the server knows but the local store does not: a view
  // remounted mid-stream (hot reload, crash recovery) comes back believing the
  // chat is new and the effect above never fetches. Once no turn is live, fetch
  // once per id regardless; the loader dedupes against what streamed in.
  const isTurnLive = useIsTurnLive(resolvedChatId)
  const settledFetchRef = useRef<string | undefined>()
  useEffect(() => {
    if (isTurnLive) {
      settledFetchRef.current = undefined
      return
    }
    if (!resolvedChatId || resolvedChatId !== activeChatId || messageCount > 0 || isFetching) return
    if (settledFetchRef.current === resolvedChatId) return
    settledFetchRef.current = resolvedChatId
    refetch()
  }, [isTurnLive, resolvedChatId, activeChatId, messageCount, isFetching, refetch])

  // Recover from stale temp ID - HIG-4293
  // When sidepanel closes before temp ID → real ID sync completes, the view context
  // keeps the stale temp ID. On reopen, use the persisted mapping to recover.
  useEffect(() => {
    const chatId = context?.chatId
    if (!chatId || isNewChat) return

    // Check if this chatId has a mapping to a real ID
    const realChatId = getRealChatId(chatId)
    if (realChatId !== chatId) {
      // Store temp ID for cleanup - don't remove mapping yet (wait for context update to propagate)
      // This prevents race condition where mapping is removed before context updates
      tempChatIdRef.current = chatId
      // Update the view context and active chat
      updateChatContext((prev) => ({ ...prev, chatId: realChatId }))
      setActiveChat(realChatId)
      // NOTE: Mapping cleanup happens in the messages load effect using tempChatIdRef
    }
  }, [context?.chatId, isNewChat, getRealChatId, updateChatContext, setActiveChat])

  // Sync the view context when activeChatId changes (e.g., new chat starts or temp ID → real ID)
  useEffect(() => {
    if (!activeChatId) {
      lastActiveChatIdRef.current = activeChatId
      return
    }

    // New chat flow: activeChatId changes twice —
    //   1st: temp UUID set by submitV1. Don't flip isNew yet so useSyncStreamingState
    //        doesn't verify a just-submitted chat before the real backend ID arrives
    //        (a stale !isStreaming response would clear the loading state for ~1 second).
    //        currentChatId already falls back to activeChatId, so nothing breaks.
    //   2nd: real UUID from metadata event. Now safe to clear isNew.
    if (isNewChat && activeChatId) {
      const lastId = lastActiveChatIdRef.current
      if (lastId && activeChatId !== lastId) {
        // Real backend ID received — flip isNew: false
        updateChatContext((prev) => (prev?.isNew ? { ...prev, chatId: activeChatId, isNew: false } : prev))
      }
      // else: first change (temp UUID) — skip context update intentionally.
      // It's this view's own send only while it is the top view.
      else if (!lastId && isTopRef.current) {
        sentChatIdRef.current = activeChatId
      }
    }
    // Existing chat - sync temp ID → real ID
    else if (context?.chatId && activeChatId !== context.chatId) {
      const contextMatchesLastActive = context.chatId === lastActiveChatIdRef.current
      if (contextMatchesLastActive) {
        // Capture the temp ID (context.chatId) before it gets overwritten - needed for mapping cleanup
        tempChatIdRef.current = context.chatId
        updateChatContext((prev) => ({ ...prev, chatId: activeChatId }))
      }
    }
    // New chat flow where context never got updated - capture temp ID from lastActiveChatIdRef
    else if (!context?.chatId && lastActiveChatIdRef.current && activeChatId !== lastActiveChatIdRef.current) {
      // lastActiveChatIdRef has the temp ID, activeChatId has the real ID
      tempChatIdRef.current = lastActiveChatIdRef.current
    }

    // Also capture temp ID on any transition (handles case where first branch runs for both temp and real)
    if (lastActiveChatIdRef.current && activeChatId !== lastActiveChatIdRef.current && !tempChatIdRef.current) {
      tempChatIdRef.current = lastActiveChatIdRef.current
    }

    lastActiveChatIdRef.current = activeChatId
  }, [activeChatId, context?.chatId, isNewChat, updateChatContext])

  return {
    activeChatId,
    messageCount,
    currentChatId,
    resolvedChatId,
    isFetched,
    isFetching,
    loadedMessageCount: loadedMessages?.messages?.length ?? 0,
  }
}
